import Close16 from '@carbon/icons-react/es/close/16';
import { Form as AntForm } from 'antd';
import { startCase } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { FilterableField } from '../fields';
import {
  GetFilterOperationTypeOptions,
  GetOperationOptionRenderer,
  GetSelectEditorComponents,
} from '../filter-box/utils';
import { FieldFilter } from '../filters';
import { Text, Fields } from './components';
import { criterion } from './criterion';
import { getDefaultCriteria } from './get-default-criteria';

const StyledText = styled(Text)`
  min-width: 40px;
`;

const NoMatches = styled.div`
  text-align: center;
`;

const Form = styled(AntForm)`
  .ant-row.ant-form-item:last-child {
    margin-right: 0;
  }
`;

interface Props {
  index?: number;
  hideMatchTypeText?: boolean;
  hideRemoveButton?: boolean;
  text: string | React.ReactNode;
  fields: FilterableField[];
  filter?: FieldFilter;
  onChange: (filter: FieldFilter) => void;
  onRemove?: () => void;
  getFilterOperationTypeOptions?: GetFilterOperationTypeOptions;
  getSelectEditorComponents?: GetSelectEditorComponents;
  getOperationOptionRenderer?: GetOperationOptionRenderer;
  className?: string;
}

export const FieldsSelect = ({
  fields,
  filter,
  onChange,
}: Pick<Props, 'fields' | 'filter' | 'onChange'>) => {
  return (
    <Fields
      width={240}
      virtual={false}
      data-cy="select.fields"
      data-cy-field={`select.fields.${filter?.field?.fieldLabel ?? '__new__'}`}
      placeholder="Select..."
      value={filter?.field?.fieldName}
      onChange={(selected) =>
        onChange(
          getDefaultCriteria(fields.find((f) => f.fieldName === selected)!),
        )
      }
      showSearch
      optionFilterProp="label"
      notFoundContent={
        <NoMatches>
          <FormattedMessage id="task_list.filters.no_fields_match" />
        </NoMatches>
      }
    >
      {fields.map((f) => (
        <Fields.Option
          key={f.fieldName}
          value={f.fieldName}
          title={f.description}
          label={f.fieldLabel}
        >
          <span
            data-cy={`select.fields.dropdown.${f.fieldName}`}
            title={f.fieldName}
          >
            {f.fieldLabel || startCase(f.fieldName)}
          </span>
        </Fields.Option>
      ))}
    </Fields>
  );
};

// TODO: Refactor out into separate criterion components
export const Criteria: FC<Props> = ({
  index = 0,
  hideMatchTypeText = false,
  hideRemoveButton = false,
  fields,
  text,
  filter,
  onChange,
  onRemove,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
  className,
}) => (
  <Form layout="inline" className={className}>
    {hideRemoveButton ? null : (
      <Form.Item>
        <Button
          type="text"
          shape="circle"
          size="small"
          icon={<Close16 />}
          style={{ visibility: onRemove != null ? 'visible' : 'hidden' }}
          onClick={onRemove}
          data-cy={`advanced.filter.remove.button.${index}`}
        />
      </Form.Item>
    )}
    {hideMatchTypeText ? null : (
      <Form.Item>
        <StyledText>{text}</StyledText>
      </Form.Item>
    )}
    <Form.Item data-cy="advanced_filter_trigger">
      <FieldsSelect fields={fields} filter={filter} onChange={onChange} />
    </Form.Item>
    {criterion({
      filter,
      onChange,
      getFilterOperationTypeOptions,
      getSelectEditorComponents,
      getOperationOptionRenderer,
    })}
  </Form>
);

export * from './grouped-criteria';
